import React, { useReducer, useState } from 'react';
import FullTable from 'components/FullTable';
import GroupTable from 'components/GroupTable';
import TableTabs from 'components/TableTabs';
import { Widget, SearchInput } from '@spins/react-ui-library';
import { ITEMS_GROUPS_TYPE, STORES_GROUPS_TYPE } from 'utils/types';
import './styles.scss';

const isGroupType = type => type === STORES_GROUPS_TYPE || type === ITEMS_GROUPS_TYPE;

const reducer = (state, action) => {
  if (action.type === 'update-total') {
    return {
      ...state,
      [action.payload.type]: action.payload.total
    };
  } else {
    return state;
  }
};

const createInitialState = (types: Array<string>) => {
  return types.reduce((accumulator, currentType) => {
    accumulator[currentType] = '?';
    return accumulator;
  }, {});
};

interface Props {
  clientId: string;
  globalFilter: any;
  selectedTab: string;
  types: Array<string>;
  onRowClick?: any;
  onTabSelect(tab: string): void;
}

/* Comment about why we render all FullTable/GroupTable components and hide some of them using
styles: we need to execute all queries to get the total items to be shown on the widget header
(queries defined inside each component). Right now the easiest way to achieve this is to return
the total after each query finish and update the state with those values, instead of doing a more
complex refactor. */

const TablesWithTabs: React.FC<Props> = ({
  clientId,
  globalFilter,
  selectedTab,
  types,
  onRowClick,
  onTabSelect
}) => {
  const [searchText, setSearchText] = useState('');
  const initialState = createInitialState(types);
  const [state, dispatch] = useReducer(reducer, initialState);

  const handleQueryFinish = (total: string, type: string) => {
    dispatch({ type: 'update-total', payload: { total, type } });
  };

  const handleGroupRowClick = (key, row, id) => {
    setSearchText('');
    onRowClick(key, row, id);
  };

  const handleRowClick = (key, row) => {
    setSearchText('');
    onRowClick(key, row);
  };

  return (
    <Widget>
      {{
        header: {
          content: {
            left: (
              <TableTabs
                selectedTab={selectedTab}
                tabs={types.map(type => ({ name: type, total: state[type] }))}
                onSelect={onTabSelect}
              />
            ),
            center: (
              <SearchInput
                className="search-field"
                placeholder="Search.."
                onChange={value => setSearchText(value)}
              />
            )
          }
        },
        body: {
          content: (
            <>
              {types.map(type => {
                if (isGroupType(type)) {
                  return (
                    <div key={type} style={{ display: selectedTab === type ? 'block' : 'none' }}>
                      <GroupTable
                        clientID={clientId}
                        globalFilter={globalFilter}
                        type={type}
                        search={searchText}
                        onRowClick={onRowClick ? handleGroupRowClick : null}
                        onQueryFinish={total => handleQueryFinish(total, type)}
                      />
                    </div>
                  );
                }
                return (
                  <div key={type} style={{ display: selectedTab === type ? 'block' : 'none' }}>
                    <FullTable
                      clientID={clientId}
                      globalFilter={globalFilter}
                      type={type}
                      search={searchText}
                      selectedTab={selectedTab}
                      onRowClick={onRowClick ? handleRowClick : null}
                      onQueryFinish={total => handleQueryFinish(total, type)}
                    />
                  </div>
                );
              })}
            </>
          )
        }
      }}
    </Widget>
  );
};

export default TablesWithTabs;
